import { useCallback } from 'react';
import { useSession } from '@texas/hooks/useSession';
import { SessionStatus } from '@texas/api/endpoints/sessionApi';
import { branchRegionsApi } from '@texas/api/endpoints/branchRegionsApi';
import { matrixConfigurationApi } from '@texas/api/endpoints/matrixConfigurationApi';
import { createContextHookValue } from '@texas/utils/contextState';
import { branchesApi } from '@texas/api/endpoints/branchesApi';
import { customersApi } from '@texas/api/endpoints/customersApi';
import { productGroupsApi } from '@texas/api/endpoints/metadata/productGroupsApi';
import { suppliersApi } from '@texas/api/endpoints/suppliersApi';
import { materialsApi } from '@texas/api/endpoints/metadata/materialsApi';
import { useCurrencies } from '@texas/hooks/metadata/useCurrencies';
import { useCustomsCodes } from '@texas/hooks/metadata/useCustomsCodes';
import { useMetadataHook } from './utils';
import { currenciesApi } from '@texas/api/endpoints/currenciesApi';
import { handlingFeesApi } from '@texas/api/endpoints/metadata/handlingFeesApi';
import { freightCostsApi } from '@texas/api/endpoints/metadata/freightCostsApi';
import { customsCodesApi } from '@texas/api/endpoints/metadata/customsCodesApi';
import { complianceDocumentDefinitionsApi } from '@texas/api/endpoints/metadata/complianceDocumentDefinitionsApi';

function useMetadataContextValue() {
  const { sessionState } = useSession();
  const organizationId =
    sessionState.status === SessionStatus.Active
      ? sessionState.session.currentUser.organizationId
      : null;
  const getBranches = useCallback(() => {
    if (organizationId === null) throw new Error('No active session');
    return branchesApi.getBranchesForOrganization(organizationId);
  }, [organizationId]);
  const branchesData = useMetadataHook(getBranches);
  const branchRegionsData = useMetadataHook(branchRegionsApi.getAll);
  const matrixGroupsData = useMetadataHook(matrixConfigurationApi.getGroups);
  const customersData = useMetadataHook(customersApi.getAll);
  const productGroupsData = useMetadataHook(productGroupsApi.getProductGroups);
  const suppliersData = useMetadataHook(suppliersApi.getAll);
  const materialsData = useMetadataHook(materialsApi.getAll);
  const currenciesData = useMetadataHook(currenciesApi.getAllCurrencies);
  const handlingFeesData = useMetadataHook(handlingFeesApi.getAll);
  const freightCostsData = useMetadataHook(freightCostsApi.getAll);
  const customsCodesData = useMetadataHook(customsCodesApi.getAll);
  const complianceDocumentDefinitionsData = useMetadataHook(
    complianceDocumentDefinitionsApi.getAll,
  );
  const [currencies] = currenciesData;
  const [customsCodes] = customsCodesData;
  const useCurrenciesData = useCurrencies({ currencies });
  const useCustomsCodesData = useCustomsCodes({ customsCodes });

  return {
    branchesData,
    branchRegionsData,
    matrixGroupsData,
    customersData,
    productGroupsData,
    suppliersData,
    materialsData,
    currenciesData,
    useCurrenciesData,
    handlingFeesData,
    freightCostsData,
    customsCodesData,
    useCustomsCodesData,
    complianceDocumentDefinitionsData,
  };
}

export const [MetadataProvider, useMetadata] = createContextHookValue(
  useMetadataContextValue,
);
